/** Every routed info-card body, as data, so the SE length budget is testable. */

export interface CardCopy {
  heading: string;
  body: string[];
}

/** Words across all paragraphs. The budget is 70-90 (see copy.test.ts).
 *
 *  NOTE: `{BRAND}` counts as one word here but substitutes to one or two at
 *  render time ("Axe" vs "Old Spice"), so bridge-adult can land one word over
 *  its measured count on screen. It is budgeted at 89, which leaves room. */
export function wordCount(body: string[]): number {
  return body.join(" ").trim().split(/\s+/).filter(Boolean).length;
}

export const CARDS: Record<string, CardCopy> = {
  "doctor-seen": {
    heading: '"Boys vary" is true. Not an all-clear.',
    body: [
      "What it means is that he falls inside the normal range for his age. That range is deliberately wide, because boys genuinely do develop at different speeds.",
      "What it does not tell you is where inside that range he was headed. Development that got held back still reads as normal, as long as it stays in the band.",
      "And nobody is screening him for this. It is not on the checkup list.",
    ],
  },
  "doctor-not": {
    heading: "It would not have come up anyway.",
    body: [
      "This is not on the checkup list. There is no standard test for it, so no pediatrician is going to raise it with you at a routine visit.",
      "Which is why it is almost always a parent who finds it first, usually because something about their own son stopped adding up.",
      "That is what you are doing right now.",
    ],
  },
  "bridge-adult": {
    heading: "Not him. Not you.",
    body: [
      "He has not been lazy and you have not been careless. {BRAND} is on almost every shelf, which is exactly why nobody flags it.",
      "It was built for a grown man, someone whose hormones finished calibrating a decade ago. Your son's are calibrating right now.",
      "Turn it over and you will find one word: fragrance. That is where phthalates hide: the only line on a US label that never has to say what is under it.",
      "You were reading the label right. That one was never printed on it.",
    ],
  },
  "bridge-natural": {
    heading: "Not him. Not you.",
    body: [
      "Going natural was the right instinct. The problem is that “natural” is not a rule. No agency defines it, nobody enforces it, and the big brands sell in that aisle too.",
      "So the front of the bottle told you what you wanted to hear, and the back never had to back it up.",
      "Turn it over and look for one word: fragrance. If it is on there, natural or not, he is getting the same thing he was getting before.",
    ],
  },
  "bridge-store": {
    heading: "Not him. Not you.",
    body: [
      "Store brands and whatever is on sale compete on one thing, and that is price. Cheap synthetic scent is the easiest place to save real money when you are making millions of bottles.",
      "That is the only reason it is in there. Nobody chose it for his skin.",
      "Turn it over and look for one word: fragrance. It is the only line on a US label that never has to say what is under it, which is why it is the one worth checking.",
    ],
  },
  "symptom-chest": {
    heading: "About the chest.",
    body: [
      "You said you assumed baby fat. Most parents do, and for plenty of boys that is exactly what it is.",
      "The kind doctors take seriously is not fat, so it does not respond to exercise or diet. Which is why the surgeons who deal with it talk about the estrogen signals a boy gets during the years testosterone should be running things, rather than about his workouts.",
      "Those signals are the part you can act on. Same part we have just been talking about.",
    ],
  },
  "symptom-general": {
    heading: "About the tiredness.",
    body: [
      "We are not going to tell you a body wash explains it. Puberty is doing a hundred things at once and we cannot see inside your son.",
      "What we can tell you is that of everything on the list you just gave us, the chemical load is the only one you can remove tomorrow.",
      "It is also the only one that is entirely in your hands, starting with his next shower.",
    ],
  },
  proof: {
    heading: "118,233 parents have already taken it out of the shower.",
    body: [
      "Most of them got here the same way you just did. Something about their own son stopped adding up, they went looking, and they ended up turning a bottle over.",
      "The word was on the back of that one too.",
    ],
  },
  "bottle-found": {
    heading: "There it is.",
    body: [
      "One word, on the back of a bottle you have been buying for years. Behind that word, the fragrance industry's own list of what is allowed inside it runs past 3,300 ingredients, and not one of them has to be printed.",
      "Call the company and ask which are in his. They will tell you the formula is proprietary.",
      "That is the entire answer you are entitled to as the person buying it.",
    ],
  },
  "bottle-clean": {
    heading: "Good. That is one down.",
    body: [
      "Now do the shampoo, the deodorant and the body spray, because it only takes one of them to put it back on him every morning.",
      "And check what replaced it on that label. “Parfum” and “aroma” are the same word wearing a different coat, and both are covered by the same exemption.",
      "If none of them have it, you are in better shape than most parents who take this.",
    ],
  },
  "bottle-later": {
    heading: "Do it tonight.",
    body: [
      "Third from the bottom is where it usually sits, in the smallest type on the bottle. Then do the shampoo and the deodorant.",
      "It will take you under a minute, and it will tell you more than anything else you read this week.",
      "We will show you what we found in the meantime, so nothing here is waiting on it.",
    ],
  },
  "obj-onepro": {
    heading: "It was measured.",
    body: [
      "Researchers at UC Berkeley switched a group of teenagers onto personal care products labeled free of these chemicals. They changed nothing else about their lives and tested them again three days later. The phthalate that carries fragrance was down 27 percent.",
      "Three days, from a product swap and nothing else.",
      "And the wash is the biggest single lever you have, because it is the only thing he uses head to toe, on all the thinnest skin at once.",
    ],
  },
  "obj-useit": {
    heading: "One test: does it smell good.",
    // 2 paragraphs here, not the usual 3-4: the "three shower tools come in
    // the order" sentence was cut by human ruling in fix round 1 because it
    // read as a kit claim against a body-wash-only offer. Deliberately not
    // repadded with new copy. See PARAGRAPH_EXCEPTIONS in copy.test.ts.
    body: [
      "That is the whole test. The scent is real cedarwood and citrus rather than a synthetic copy of one, and it lathers like the wash he is already used to, so nothing about his shower changes except what is in the bottle.",
      "If he will not touch it, you have 60 days.",
    ],
  },
  "obj-clean": {
    heading: "Agreed. Do not trust the front.",
    body: [
      "Three checks, all on the back. One, is “fragrance” on the label. If it is, put it back. Two, is it a big brand. At millions of bottles, cheap synthetic scent saves real money, and that is the only reason it goes in.",
      "Three, where was it made. In the US nobody reviews a new chemical before it goes into a body wash and onto a shelf. Australia reviews them first.",
      "Take those three into any store and buy whatever passes. We are not precious about which bottle ends up in his shower.",
    ],
  },
  "obj-price": {
    heading: "Sixty days, any reason.",
    body: [
      "It is one bottle, going into a shower he is already taking, replacing one you are already buying every few weeks. The line item barely changes, only what is in it.",
      "And if it does not do what we said it would, you send it back inside 60 days and we cover the return shipping.",
      "Not 30. Not 14. Sixty.",
    ],
  },
  "obj-ready": {
    heading: "Then we will not slow you down.",
    body: [
      "Most parents arrive here needing convincing. You do not, so we will skip the part where we talk you into it.",
      "One thing worth knowing before his profile loads. The wash is the biggest single lever you have, because it is the only thing he uses head to toe, every day, on all the thinnest skin at once.",
      "Start there. The shampoo and the deodorant can wait until you are ready.",
    ],
  },
};
